import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  ChevronLeft, Camera, Upload, X, FileText, AlertTriangle, Printer, Plus, Check,
} from 'lucide-react';
import { openAssetLabelPdf } from '@/lib/api';

const CONDITIONS = ['New', 'Good', 'Fair', 'Poor'];

async function fileToDataUrl(file, maxDim = 1600, quality = 0.82) {
  // Read as data URL first
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  // Resize if it's an image to keep payload light
  if (!file.type.startsWith('image/')) return dataUrl;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export default function NewAssetPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dup = location.state?.duplicate || null;
  const { user, hasPerm } = useAuth();
  const [cats, setCats] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [users, setUsers] = useState([]);
  const [owningEntities, setOwningEntities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [serialError, setSerialError] = useState('');
  const [savedAsset, setSavedAsset] = useState(null);

  const canSeeMoney = hasPerm('see_money');
  const canList = hasPerm('assets_list');

  const [form, setForm] = useState({
    name: dup?.name || '',
    category_id: dup?.category_id || '',
    brand: dup?.brand || '',
    model: dup?.model || '',
    serial_no: '',    // never duplicate serial
    purchase_date: '', purchase_cost: '', vendor: '', warranty_end: '',
    photos: [],        // never duplicate photos
    invoice_file: null, invoice_filename: null,
    condition: dup?.condition || 'New',
    accessories: dup?.accessories || '',
    notes: dup?.notes || '',
    owning_entity: dup?.owning_entity || '',
    is_fixed: !!dup?.is_fixed,
    holder_type: 'godown', holder_id: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, g, u, s] = await Promise.all([
          api.get('/assets/categories'),
          api.get('/assets/godowns'),
          api.get('/assets/users').catch(() => ({ data: [] })),
          api.get('/assets/settings').catch(() => ({ data: { owning_entities: [] } })),
        ]);
        setCats(c.data || []);
        setGodowns(g.data || []);
        setUsers(u.data || []);
        setOwningEntities(s.data?.owning_entities || []);
        const defaultG = (g.data || []).find((x) => x.is_default) || (g.data || [])[0];
        if (defaultG) setForm((f) => ({ ...f, holder_id: f.holder_id || defaultG.id }));
        if ((s.data?.owning_entities || []).length) setForm((f) => ({ ...f, owning_entity: f.owning_entity || s.data.owning_entities[0] }));
      } catch (e) {
        toast.error('Failed to load setup');
      }
    })();
  }, []);

  if (!canList) {
    return (
      <div className="max-w-[700px] mx-auto px-4 py-10">
        <Card className="shine-card p-6 rounded-xl text-center" data-testid="newasset-forbidden">
          <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
          <div className="text-sm font-semibold text-ink">Not allowed</div>
          <div className="text-xs text-ink-2 mt-1">You don&apos;t have permission to list assets. Ask the Owner to grant it under Users → Access tab.</div>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/assets')}>Back to Assets</Button>
        </Card>
      </div>
    );
  }

  const addPhoto = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file, 1600, 0.82);
    setForm((f) => ({ ...f, photos: [...(f.photos || []), url].slice(0, 4) }));
  };

  const removePhoto = (i) => {
    setForm((f) => ({ ...f, photos: (f.photos || []).filter((_, idx) => idx !== i) }));
  };

  const addInvoice = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file, 2400, 0.85);
    setForm((f) => ({ ...f, invoice_file: url, invoice_filename: file.name }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.category_id)  return toast.error('Pick a category');
    if (!form.holder_id)    return toast.error('Pick where it is now');
    setSaving(true);
    try {
      const payload = {
        ...form,
        purchase_cost: form.purchase_cost === '' ? null : Number(form.purchase_cost),
      };
      const { data } = await api.post('/assets', payload);
      toast.success('Asset listed');
      setSavedAsset(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Save failed';
      if (e?.response?.status === 409) setSerialError(detail);
      toast.error(detail);
    } finally { setSaving(false); }
  };

  const closeSuccessAndStartAnother = () => {
    setSavedAsset(null);
    setSerialError('');
    setForm((f) => ({
      ...f, name: '', brand: '', model: '', serial_no: '',
      purchase_cost: '', vendor: '', warranty_end: '',
      photos: [], invoice_file: null, invoice_filename: null,
      condition: 'New', accessories: '', notes: '', is_fixed: false,
    }));
  };

  const defaultGodown = godowns.find((g) => g.is_default) || godowns[0];

  return (
    <div className="max-w-[920px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="new-asset-page">
      <Link to="/assets" className="text-xs text-ink-2 inline-flex items-center gap-1 hover:text-ink">
        <ChevronLeft className="h-3 w-3" /> All assets
      </Link>
      <h1 className="text-xl font-semibold text-ink">Add asset</h1>

      {/* 1. What it is */}
      <Card className="shine-card p-5 rounded-xl space-y-3">
        <div className="text-sm font-semibold">What it is</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">Asset name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Canon R5 body" data-testid="asset-name" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
              <SelectTrigger data-testid="asset-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Brand</Label>
            <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Canon" />
          </div>
          <div>
            <Label className="text-xs">Model</Label>
            <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="EOS R5" />
          </div>
          <div>
            <Label className="text-xs">Serial number</Label>
            <Input value={form.serial_no}
                   onChange={(e) => { setForm({ ...form, serial_no: e.target.value }); setSerialError(''); }}
                   placeholder="013021004871"
                   className={serialError ? 'border-amber-400 focus-visible:ring-amber-400' : ''}
                   data-testid="asset-serial" />
            {serialError && (
              <p className="text-[11px] text-amber-700 mt-1" data-testid="asset-serial-error">{serialError}</p>
            )}
          </div>
        </div>
      </Card>

      {/* 2. Purchase */}
      <Card className="shine-card p-5 rounded-xl space-y-3">
        <div className="text-sm font-semibold">Purchase</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Purchase date</Label>
            <Input type="date" value={form.purchase_date || ''} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">
              Purchase cost {canSeeMoney ? '' : <span className="text-ink-2">(hidden)</span>}
            </Label>
            <Input type="number" min="0" step="1" inputMode="numeric"
                   value={form.purchase_cost}
                   disabled={!canSeeMoney}
                   onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} placeholder="₹" />
          </div>
          <div>
            <Label className="text-xs">Vendor</Label>
            <Input value={form.vendor} disabled={!canSeeMoney}
                   onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Vendor name" />
          </div>
          <div>
            <Label className="text-xs">Warranty end</Label>
            <Input type="date" value={form.warranty_end || ''} onChange={(e) => setForm({ ...form, warranty_end: e.target.value })} />
          </div>
        </div>
      </Card>

      {/* 3. Condition + Custody */}
      <Card className="shine-card p-5 rounded-xl space-y-3">
        <div className="text-sm font-semibold">Condition & custody</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Where it is now</Label>
            <div className="flex gap-2">
              <Select value={form.holder_type} onValueChange={(v) => setForm({ ...form, holder_type: v, holder_id: '' })}>
                <SelectTrigger className="w-[120px]" data-testid="holder-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="godown">Godown</SelectItem>
                  <SelectItem value="user">Person</SelectItem>
                </SelectContent>
              </Select>
              <Select value={form.holder_id} onValueChange={(v) => setForm({ ...form, holder_id: v })}>
                <SelectTrigger className="flex-1" data-testid="holder-id"><SelectValue placeholder={form.holder_type === 'godown' ? 'Pick a godown' : 'Pick a person'} /></SelectTrigger>
                <SelectContent>
                  {form.holder_type === 'godown'
                    ? godowns.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)
                    : users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Condition</Label>
            <div className="flex gap-1">
              {CONDITIONS.map((c) => (
                <button key={c} type="button"
                        onClick={() => setForm({ ...form, condition: c })}
                        className={`flex-1 py-1.5 text-xs rounded-md border ${form.condition === c ? 'border-ink bg-ink text-white' : 'border-ink/15 text-ink hover:bg-page'}`}
                        data-testid={`condition-${c.toLowerCase()}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Accessories included</Label>
            <Input value={form.accessories} onChange={(e) => setForm({ ...form, accessories: e.target.value })}
                   placeholder="2 batteries, Charger, Strap, Body cap" />
          </div>
          <div>
            <Label className="text-xs">Owning company</Label>
            <Select value={form.owning_entity} onValueChange={(v) => setForm({ ...form, owning_entity: v })}>
              <SelectTrigger data-testid="owning-entity"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>{owningEntities.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Fixed item?</Label>
            <div className="flex items-center gap-2 h-10">
              <Switch checked={!!form.is_fixed}
                      onCheckedChange={(v) => setForm({ ...form, is_fixed: !!v })}
                      data-testid="is-fixed" />
              <span className="text-xs text-ink-2">
                {form.is_fixed ? 'Yes — AC/CCTV/furniture, stays put' : 'No — it can be taken out'}
              </span>
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Bought for the studio shoots. Keep in the dry cabinet." />
          </div>
        </div>
      </Card>

      {/* 4. Photos */}
      <Card className="shine-card p-5 rounded-xl space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Photos</div>
          <div className="text-[11px] text-ink-2">{form.photos.length} of 4</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {form.photos.map((p, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-ink/10">
              <img src={p} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5"
                      aria-label="Remove">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {form.photos.length < 4 && (
            <label className="aspect-square rounded-lg border border-dashed border-ink/20 flex flex-col items-center justify-center text-ink-2 hover:text-ink hover:border-ink/40 cursor-pointer">
              <Camera className="h-5 w-5 mb-1" />
              <span className="text-[10px]">+ Add</span>
              <input type="file" accept="image/*" capture="environment" className="hidden"
                     onChange={(e) => addPhoto(e.target.files?.[0])}
                     data-testid={`add-photo-${form.photos.length}`} />
            </label>
          )}
        </div>
      </Card>

      {/* 5. Invoice */}
      <Card className="shine-card p-5 rounded-xl space-y-3">
        <div className="text-sm font-semibold">Invoice file {canSeeMoney ? '' : <span className="text-ink-2 text-xs font-normal">(hidden for your role)</span>}</div>
        {form.invoice_file ? (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-page border border-ink/10">
            <FileText className="h-4 w-4 text-ink-2" />
            <span className="text-sm text-ink flex-1 truncate">{form.invoice_filename || 'Invoice attached'}</span>
            <button type="button" className="text-ink-2 hover:text-ink"
                    onClick={() => setForm({ ...form, invoice_file: null, invoice_filename: null })}>
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : canSeeMoney ? (
          <label className="inline-flex items-center gap-2 text-xs text-ink-2 cursor-pointer px-3 py-2 rounded-lg border border-dashed border-ink/20 hover:text-ink hover:border-ink/40">
            <Upload className="h-3.5 w-3.5" /> Attach PDF or image
            <input type="file" accept=".pdf,image/*" className="hidden"
                   onChange={(e) => addInvoice(e.target.files?.[0])} />
          </label>
        ) : (
          <div className="text-xs text-ink-2">Attachments can be added by users with the “See cost” permission.</div>
        )}
      </Card>

      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-ink-2">Only people allowed to list assets can save. The Asset ID and QR label are created on save.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/assets')} data-testid="asset-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="asset-save">
            {saving ? 'Saving…' : 'Save asset'}
          </Button>
        </div>
      </div>

      {/* Success modal */}
      <Dialog open={!!savedAsset} onOpenChange={(o) => { if (!o) setSavedAsset(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="asset-saved-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Check className="h-5 w-5 text-emerald-600" /> Asset listed</DialogTitle>
          </DialogHeader>
          {savedAsset && (
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-ink font-medium">{savedAsset.name}</span>
                <span className="text-ink-2"> is now in {savedAsset.holder_name || 'the godown'}.</span>
              </div>
              <div className="rounded-lg border border-ink/10 bg-page p-3 flex items-center gap-3">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/${savedAsset.asset_id}/qr.png?t=${Date.now()}`}
                     alt="QR" className="w-20 h-20 bg-white rounded border border-ink/10 p-1" />
                <div>
                  <div className="text-xs text-ink-2">Asset ID</div>
                  <div className="font-mono font-semibold text-ink">{savedAsset.asset_id}</div>
                  <div className="text-xs text-ink-2 mt-1">{savedAsset.owning_entity}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm"
                        onClick={() => openAssetLabelPdf(savedAsset.asset_id)}
                        data-testid="saved-print-label">
                  <Printer className="h-3.5 w-3.5 mr-1" /> Print label
                </Button>
                <Button variant="outline" size="sm" onClick={closeSuccessAndStartAnother} data-testid="saved-add-another">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add another
                </Button>
                <Button size="sm" onClick={() => navigate(`/assets/${savedAsset.asset_id}`)} data-testid="saved-view">
                  View asset
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
